import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { SuggestCategoryModal } from "@/components/suggest-category-modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  Newspaper, 
  Plus, 
  Loader2,
  FileText,
  Sparkles,
  Copy,
  Check,
  Clock,
  Building2,
} from "lucide-react";
import type { Company } from "@shared/schema";

interface PressReleaseSubmissionWithFormData {
  id: string;
  type: string;
  formData: string;
  generatedContent: string | null;
  status: string | null;
  companyId: string | null;
  createdAt: Date | string | null;
}

interface DimensionWithOptions {
  id: string;
  name: string;
  displayName: string;
  options: { id: string; name: string; displayName: string; acronym: string | null; parentId: string | null; depth: number }[];
}

const pressReleaseSchema = z.object({
  productName: z.string().min(1, "Product name is required"),
  companyName: z.string().min(1, "Company name is required"),
  onBehalfOfCompanyId: z.string().optional(),
  headline: z.string().min(10, "Headline must be at least 10 characters"),
  summary: z.string().min(20, "Summary must be at least 20 characters"),
  keyFeatures: z.string().min(10, "List at least one key feature"),
  challengesSolved: z.string().optional(),
  competitiveDifferentiation: z.string().optional(),
  trustSignals: z.string().optional(),
  targetMarket: z.string().optional(),
  availabilityDate: z.string().optional(),
  pricing: z.string().optional(),
  contactName: z.string().min(1, "Contact name is required"),
  contactEmail: z.string().email("Valid email required"),
  contactPhone: z.string().optional(),
});

type PressReleaseFormValues = z.infer<typeof pressReleaseSchema>;

export default function UserPressReleases() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("create");
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: affiliations = [] } = useQuery<{ company: Company; status: string }[]>({
    queryKey: ["/api/affiliations/my"],
  });

  const { data: submissions = [], isLoading: submissionsLoading } = useQuery<PressReleaseSubmissionWithFormData[]>({
    queryKey: ["/api/press-release-submissions/my"],
  });

  const approvedAffiliations = affiliations.filter(
    a => a.status === "approved" && a.company?.id
  );

  const [suggestChallengeOpen, setSuggestChallengeOpen] = useState(false);

  const { data: dimensionsWithOptions = [] } = useQuery<DimensionWithOptions[]>({
    queryKey: ["/api/categories/dimensions-with-options"],
  });
  const challengesDimension = dimensionsWithOptions.find(d => d.name === "challenges");
  const challengeOptions = challengesDimension?.options ?? [];

  const form = useForm<PressReleaseFormValues>({
    resolver: zodResolver(pressReleaseSchema),
    defaultValues: {
      productName: "",
      companyName: "",
      onBehalfOfCompanyId: undefined,
      headline: "",
      summary: "",
      keyFeatures: "",
      challengesSolved: "",
      competitiveDifferentiation: "",
      trustSignals: "",
      targetMarket: "",
      availabilityDate: "",
      pricing: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (data: PressReleaseFormValues) => {
      const response = await apiRequest("POST", "/api/press-release-submissions", data);
      const submission = await response.json();
      const generateResponse = await apiRequest("POST", `/api/press-release-submissions/${submission.id}/generate`);
      return generateResponse.json();
    },
    onSuccess: (data) => {
      setGeneratedContent(data.generatedContent);
      queryClient.invalidateQueries({ queryKey: ["/api/press-release-submissions/my"] });
      toast({
        title: "Press Release Generated",
        description: "Your press release has been created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate press release",
        variant: "destructive",
      });
    },
  });

  const handleCopy = async () => {
    if (generatedContent) {
      await navigator.clipboard.writeText(generatedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "generated":
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><Check className="h-3 w-3 mr-1" />Generated</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "generating":
        return <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" data-testid="text-press-releases-title">Press Releases</h1>
        <p className="text-muted-foreground">
          Create and manage press releases for your products
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="create" data-testid="tab-create">
            <Plus className="h-4 w-4 mr-2" />
            Create New
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="h-4 w-4 mr-2" />
            My Submissions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Newspaper className="h-5 w-5" />
                  New Product Press Release
                </CardTitle>
                <CardDescription>
                  Fill in the details and we'll generate a professional press release
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit((data) => generateMutation.mutate(data))} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="productName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Product Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., IndustrialPro X500" {...field} data-testid="input-product-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Your Company</FormLabel>
                          <FormControl>
                            <Input placeholder="Company issuing the release" {...field} data-testid="input-company-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {approvedAffiliations.length > 0 && (
                      <FormField
                        control={form.control}
                        name="onBehalfOfCompanyId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>On Behalf Of (Optional)</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || ""}>
                              <FormControl>
                                <SelectTrigger data-testid="select-on-behalf-of">
                                  <SelectValue placeholder="Select client company" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="self">None - My own company</SelectItem>
                                {approvedAffiliations.map((aff) => 
                                  aff.company?.id ? (
                                    <SelectItem key={aff.company.id} value={aff.company.id}>
                                      {aff.company.name}
                                    </SelectItem>
                                  ) : null
                                )}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              If you're creating this for a client company
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="headline"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Headline</FormLabel>
                          <FormControl>
                            <Input placeholder="Main announcement headline" {...field} data-testid="input-headline" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="summary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Summary</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Brief description of the product and its significance" 
                              rows={3}
                              {...field} 
                              data-testid="input-summary"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="keyFeatures"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Key Features</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="List key features, one per line" 
                              rows={3}
                              {...field} 
                              data-testid="input-key-features"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />
                    <p className="text-sm font-medium text-muted-foreground">Content Style Questions</p>
                    
                    <FormField
                      control={form.control}
                      name="challengesSolved"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Challenges Solved</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="What customer challenges does this product address? Include real-world pain points your target audience faces."
                              rows={3}
                              {...field} 
                              data-testid="input-challenges-solved"
                            />
                          </FormControl>
                          <FormDescription className="flex flex-col gap-1">
                            {challengeOptions.length > 0 && (
                              <span className="text-xs">
                                Common challenges: {challengeOptions.filter(c => c.depth === 2).slice(0, 5).map(c => c.displayName).join(", ")}
                                {challengeOptions.filter(c => c.depth === 2).length > 5 && "..."}
                              </span>
                            )}
                            {challengesDimension && (
                              <button
                                type="button"
                                className="text-xs text-primary underline underline-offset-2 hover:no-underline w-fit"
                                onClick={() => setSuggestChallengeOpen(true)}
                                data-testid="button-suggest-challenge"
                              >
                                Don't see the right challenge? Suggest one →
                              </button>
                            )}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="competitiveDifferentiation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Competitive Differentiation</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="What sets this product apart from competitors? Include unique technical innovations, approach, or value proposition."
                              rows={3}
                              {...field} 
                              data-testid="input-competitive-differentiation"
                            />
                          </FormControl>
                          <FormDescription>
                            Focus on concrete differentiators, not generic claims
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="trustSignals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Trust Signals</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Include customer testimonials, certifications, awards, industry recognition, or proof points that build credibility."
                              rows={3}
                              {...field} 
                              data-testid="input-trust-signals"
                            />
                          </FormControl>
                          <FormDescription>
                            Amplify credibility with concrete evidence and endorsements
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="targetMarket"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Target Market</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Industrial automation" {...field} data-testid="input-target-market" />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="availabilityDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Availability Date</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} data-testid="input-availability-date" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="pricing"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pricing (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Starting at $499" {...field} data-testid="input-pricing" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Separator />

                    <p className="text-sm font-medium">Contact Information</p>

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="contactName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Media contact name" {...field} data-testid="input-contact-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contactEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="press@company.com" {...field} data-testid="input-contact-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="contactPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Phone (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="+1 (555) 000-0000" {...field} data-testid="input-contact-phone" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={generateMutation.isPending}
                      data-testid="button-generate-press-release"
                    >
                      {generateMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Press Release
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Generated Content
                  </span>
                  {generatedContent && (
                    <Button variant="ghost" size="sm" onClick={handleCopy} data-testid="button-copy">
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedContent ? (
                  <ScrollArea className="h-[500px]">
                    <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                      {generatedContent}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sparkles className="h-8 w-8 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">
                      Fill out the form and click "Generate" to create your press release
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {submissionsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Submissions Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first press release to see it here
                </p>
                <Button onClick={() => setActiveTab("create")} data-testid="button-create-first">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Press Release
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => {
                const formData = JSON.parse(submission.formData || "{}");
                return (
                  <Card key={submission.id} data-testid={`card-submission-${submission.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-base">{formData.productName || "Untitled"}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Building2 className="h-3 w-3" />
                            {formData.companyName || "Unknown Company"}
                          </CardDescription>
                        </div>
                        {getStatusBadge(submission.status || "pending")}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {formData.headline || "No headline"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Created {submission.createdAt ? new Date(submission.createdAt).toLocaleDateString() : "Unknown"}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {challengesDimension && (
        <SuggestCategoryModal
          open={suggestChallengeOpen}
          onOpenChange={setSuggestChallengeOpen}
          dimensionId={challengesDimension.id}
        />
      )}
    </div>
  );
}
